/**
 * Usage telemetry beacon.
 *
 * Fires a best-effort POST to the backend's /api/usage-events endpoint after
 * each Azure OpenAI completion so the analytics dashboard can aggregate token
 * consumption per user / agent / model. Errors are swallowed — telemetry must
 * never break the chat flow.
 */

import { getBackendUrl } from '../config/runtimeConfig';
import { getBackendHeaders } from './backendAuth';

export interface UsageEvent {
  agentId?: string | null;
  conversationId?: string | null;
  messageId?: string | null;
  model?: string | null;
  /** The model that actually answered (Azure response `model`); priced over `model`. */
  servedModel?: string | null;
  promptTokens: number;
  completionTokens: number;
  finishReason?: string | null;
  latencyMs?: number | null;
  traceId?: string | null;
  /**
   * Distinguishes the user-facing completion ('chat', default) from
   * auxiliary calls like history summarization ('summarize'). Cost rollups
   * sum all rows; request-count / completion-quality metrics filter to
   * 'chat' so auxiliary calls don't skew them.
   */
  callType?: 'chat' | 'summarize';
}

export async function sendUsageEvent(event: UsageEvent): Promise<void> {
  try {
    const backendUrl = getBackendUrl();
    if (!backendUrl) return;
    await fetch(`${backendUrl}/usage-events`, {
      method: 'POST',
      headers: await getBackendHeaders(),
      body: JSON.stringify(event),
      keepalive: true,
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'unknown';
    console.warn('[usageTelemetry] Failed to send usage event:', message);
  }
}
